import React, { useState, useEffect } from 'react';
import { ShieldCheck, Eye, RefreshCw, ChevronRight } from 'lucide-react';
import { fetchKycQueue, processKycAction } from '../utils/api';

type KycStatus = 'pending' | 'verified' | 'failed';

interface KycItem {
  id: string;
  userId: string;
  userName: string;
  phone: string;
  type: 'nin' | 'bvn' | 'selfie' | 'document';
  status: KycStatus;
  submittedAt: string;
  trustScore: number;
  kycLevel: number;
  nin?: string;
  bvn?: string;
  selfieUrl?: string;
}

const TYPE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  nin:      { label: 'NIN (Dojah)',  color: '#60a5fa', bg: 'rgba(59,130,246,0.1)' },
  bvn:      { label: 'BVN (Dojah)',  color: '#a78bfa', bg: 'rgba(139,92,246,0.1)' },
  selfie:   { label: 'Face Liveness', color: '#34d399', bg: 'rgba(16,185,129,0.1)' },
  document: { label: 'Govt ID',       color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
};

export const KYCQueuePage: React.FC = () => {
  const [items, setItems] = useState<KycItem[]>([]);
  const [filterStatus, setFilterStatus] = useState<KycStatus | 'all'>('pending');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<KycItem | null>(null);
  const [rejectReason, setRejectReason] = useState('Biometric mismatch');
  const [loading, setLoading] = useState(true);
  const [dataMode, setDataMode] = useState<'test' | 'live'>('live');

  useEffect(() => {
    loadQueue();
    const handleModeChanged = () => {
      loadQueue();
    };
    window.addEventListener('system-mode-changed', handleModeChanged);
    return () => window.removeEventListener('system-mode-changed', handleModeChanged);
  }, []);

  const loadQueue = async () => {
    setLoading(true);
    const res = await fetchKycQueue();
    if (res?.queue) {
      setItems(res.queue);
      setSelectedItem(res.queue[0] || null);
    }
    if (res?.mode) {
      setDataMode(res.mode);
    }
    setLoading(false);
  };

  const filtered = items.filter((i) => filterStatus === 'all' || i.status === filterStatus);
  const pendingCount = items.filter((i) => i.status === 'pending').length;

  const handle = async (id: string, action: 'verify' | 'fail') => {
    setProcessingId(id);
    await processKycAction(id, action, action === 'fail' ? rejectReason : undefined);
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status: action === 'verify' ? 'verified' : 'failed' } : i))
    );
    if (selectedItem?.id === id) {
      setSelectedItem((prev) => (prev ? { ...prev, status: action === 'verify' ? 'verified' : 'failed' } : null));
    }
    setProcessingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-['Outfit']">
              KYC Identity Verification Queue
            </h1>
            {dataMode === 'test' ? (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                🧪 Test Sandbox Submissions
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                ⚡ Live D1 KYC Records
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            Review Dojah identity validations, National Identification Numbers (NIN), and facial liveness
            {pendingCount > 0 && (
              <span className="ml-2 px-2.5 py-0.5 text-xs font-bold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                {pendingCount} Pending
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadQueue}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-300 hover:text-white transition-colors"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>

          <select
            className="px-3 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
          >
            <option value="all">All Submissions</option>
            <option value="pending">Pending Only</option>
            <option value="verified">Verified Only</option>
            <option value="failed">Failed Only</option>
          </select>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Pending Review', value: items.filter((i) => i.status === 'pending').length, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
          { label: 'Verified Identities', value: items.filter((i) => i.status === 'verified').length, color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
          { label: 'Rejected / Failed', value: items.filter((i) => i.status === 'failed').length, color: '#ef5350', bg: 'rgba(239,83,80,0.1)' },
        ].map((s, i) => (
          <div key={i} className="p-4 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
            <p className="text-2xl font-extrabold font-['Outfit'] mb-1" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-zinc-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Main Grid: Queue on Left, Sticky Inspector on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Queue Table */}
        <div className="lg:col-span-2 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#141428] text-xs font-bold uppercase tracking-wider text-zinc-400 border-b border-[#1e1e3a]">
                <tr>
                  <th className="px-6 py-3">Applicant</th>
                  <th className="px-6 py-3">Method</th>
                  <th className="px-6 py-3">Trust Score</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e1e3a]">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-xs text-zinc-500">
                      No KYC verification submissions in this queue. When users complete biometric face liveness or submit ID records, they will appear here.
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => {
                    const isSelected = selectedItem?.id === item.id;
                    return (
                      <tr
                        key={item.id}
                        onClick={() => setSelectedItem(item)}
                        className={`cursor-pointer transition-colors ${
                          isSelected ? 'bg-purple-600/10' : 'hover:bg-[#141428]/50'
                        }`}
                      >
                        <td className="px-6 py-4">
                          <p className="font-semibold text-white">{item.userName}</p>
                          <p className="text-xs text-zinc-400">{item.phone}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="px-2.5 py-1 text-xs font-semibold rounded-lg"
                            style={{
                              backgroundColor: TYPE_CONFIG[item.type]?.bg || '#141428',
                              color: TYPE_CONFIG[item.type]?.color || '#FFF',
                            }}
                          >
                            {TYPE_CONFIG[item.type]?.label || item.type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-zinc-800 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${item.trustScore}%`,
                                  backgroundColor: item.trustScore >= 75 ? '#10b981' : item.trustScore >= 50 ? '#f59e0b' : '#ef5350',
                                }}
                              />
                            </div>
                            <span className="text-xs font-mono font-bold text-white">{item.trustScore}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-1 text-xs font-bold rounded-lg ${
                              item.status === 'verified'
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                : item.status === 'pending'
                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                : 'bg-red-500/10 text-red-400 border border-red-500/20'
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-zinc-400">{item.submittedAt}</td>
                        <td className="px-4 py-4 text-zinc-500">
                          <ChevronRight size={16} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Biometric Review Inspector Panel */}
        <div className="rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] p-6 flex flex-col justify-between">
          {selectedItem ? (
            <div className="space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-[#1e1e3a]">
                <h2 className="text-base font-bold text-white">Biometric Inspector</h2>
                <span className="text-xs font-mono text-zinc-400">{selectedItem.id}</span>
              </div>

              <div>
                <p className="text-xs text-zinc-400 mb-1">Applicant Profile</p>
                <p className="text-lg font-bold text-white">{selectedItem.userName}</p>
                <p className="text-xs text-zinc-400">{selectedItem.phone}</p>
              </div>

              {selectedItem.selfieUrl && (
                <div>
                  <p className="text-xs text-zinc-400 mb-2">Live Camera Capture (MediaPipe)</p>
                  <div className="relative rounded-2xl overflow-hidden border border-[#1e1e3a] bg-black">
                    <img
                      src={selectedItem.selfieUrl}
                      alt="Selfie verification"
                      className="w-full h-44 object-cover"
                    />
                    <div className="absolute top-2 right-2 px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-500/80 text-white backdrop-blur-sm">
                      96% Liveness Match
                    </div>
                  </div>
                </div>
              )}

              {selectedItem.nin && (
                <div className="p-3 rounded-xl bg-[#141428] border border-[#1e1e3a]">
                  <p className="text-xs text-zinc-400 mb-1">National Identity Number (NIN)</p>
                  <p className="font-mono text-base font-bold text-blue-400 tracking-wider">
                    {selectedItem.nin}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-1">Dojah API Status: Valid NIN slip found</p>
                </div>
              )}

              {selectedItem.bvn && (
                <div className="p-3 rounded-xl bg-[#141428] border border-[#1e1e3a]">
                  <p className="text-xs text-zinc-400 mb-1">Bank Verification Number (BVN)</p>
                  <p className="font-mono text-base font-bold text-purple-400 tracking-wider">
                    {selectedItem.bvn}
                  </p>
                </div>
              )}

              {selectedItem.status === 'pending' && (
                <div className="space-y-3 pt-2">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-400 mb-1">Rejection Reason</label>
                    <select
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl text-xs bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none"
                    >
                      <option value="Biometric mismatch">Biometric selfie mismatch</option>
                      <option value="Blurry selfie capture">Blurry selfie capture</option>
                      <option value="Invalid NIN digits">Invalid NIN digits</option>
                      <option value="Suspected impersonation">Suspected impersonation</option>
                    </select>
                  </div>

                  <div className="flex gap-2">
                    <button
                      disabled={processingId === selectedItem.id}
                      onClick={() => handle(selectedItem.id, 'verify')}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                    >
                      <ShieldCheck size={14} />
                      Approve & Grant Level {selectedItem.kycLevel || 2}
                    </button>
                    <button
                      disabled={processingId === selectedItem.id}
                      onClick={() => handle(selectedItem.id, 'fail')}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-500">
              <Eye size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-xs">Select an applicant to review</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
